/**
 * @file 学ぶ人が取り込んだ学びの計画の一覧を読み書きする hook。
 *
 * @what: 学ぶ人ごとに一覧を1回読み、保存 (取り込み・書き換え・終える) の結果で一覧の該当の1件を
 *   置き換える。一覧が空なのは正常系で、まだ何も取り込んでいない状態を表す。
 * @why: 計画の面はこの一覧だけを出す。取り込んでいないカリキュラムは出さない (代表の決定、
 *   2026-09-11)。カリキュラムの一覧は別に参考書ルートを読む。
 * @failure: 取得失敗は `error`、保存失敗は `saveError` に1行を入れる (詳細は console のみ)。
 */

import { useCallback, useEffect, useState } from "react";
import {
  fetchLearningPlans,
  putLearningPlan,
  type StudyLearningPlan,
  type StudyLearningPlanInput,
} from "../api/learningPlans";

export type LearningPlansState =
  | { status: "loading" }
  | { status: "ready"; plans: StudyLearningPlan[] }
  | { status: "error" };

export function useLearningPlans(familyId: string, personId: string | null, enabled: boolean) {
  const [state, setState] = useState<LearningPlansState>({ status: "loading" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !personId) return;
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchLearningPlans(familyId, personId, controller.signal)
      .then((plans) => {
        if (!controller.signal.aborted) setState({ status: "ready", plans });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load learning plans", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, personId, enabled]);

  const save = useCallback(
    async (seriesId: string, input: StudyLearningPlanInput): Promise<boolean> => {
      if (!personId) return false;
      setSaving(true);
      setSaveError(null);
      try {
        const saved = await putLearningPlan(familyId, personId, seriesId, input);
        setState((prev) => {
          // 一覧を読み終える前に保存が返ることがあるので、その場合は1件だけの一覧にする。
          if (prev.status !== "ready") return { status: "ready", plans: [saved] };
          const next = prev.plans.filter((p) => p.id !== saved.id);
          next.push(saved);
          // 取り込んだ順に並べ直す (サーバの一覧と同じ並び)。
          next.sort((a, b) =>
            a.created_at === b.created_at
              ? a.id.localeCompare(b.id)
              : a.created_at.localeCompare(b.created_at),
          );
          return { status: "ready", plans: next };
        });
        return true;
      } catch (err) {
        console.error("[study] failed to save learning plan", err);
        setSaveError("保存できませんでした。時間を置いてもう一度お試しください。");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [familyId, personId],
  );

  return { state, save, saving, saveError };
}
